import {
  Badge,
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Dataset } from "../types";
import {
  DrawerComponent,
  useDatasetViewerStore,
} from "../stores/DatasetViewerStore";
import { FilterAltOutlined } from "@mui/icons-material";

import DatasetHeaderActions from "./DatasetHeaderActions";
import DatasetViewerSearch from "./DatasetViewerSearch";
import SearchIndexInfo from "./SearchIndexInfo";

// Breakpoint for responsive filter behavior
// Below this: show filter button + modal
// Above this: filters always visible in sidebar
export const FILTER_SIDEBAR_BREAKPOINT = "md" as const;

type DatasetToolBarProps = {
  dataset: Dataset;
  overrideDatasetId?: string;
  hideElements?: {
    header?: boolean;
    uploadButton?: boolean;
    sideDrawer?: boolean;
    downloadButton?: boolean;
  };
  searchText?: string;
  /** Callback when search text changes */
  onSearchChange?: (value: string) => void;
  /** Override filter click handler for new Amazon-style filters */
  onFilterClick?: () => void;
  /** Whether the new filter sidebar is active */
  isNewFilterActive?: boolean;
  /** Number of active filters (for badge display) */
  activeFilterCount?: number;
  /** Whether to hide the filter button (e.g., when filters are always visible on large screens) */
  hideFilterButton?: boolean;
};

const DatasetToolBar = ({
  dataset,
  overrideDatasetId,
  hideElements,
  searchText = "",
  onSearchChange,
  onFilterClick,
  isNewFilterActive,
  activeFilterCount,
}: DatasetToolBarProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // Show filter button for all screens below desktop (lg, 1200px)
  const isBelowDesktop = useMediaQuery(theme.breakpoints.down("lg"));
  const { toggleDrawer, hasActiveFilters } = useDatasetViewerStore();
  const activeDrawerComponents = useDatasetViewerStore(
    (s) => s.activeDrawerComponents,
  );
  const hasFilters = hasActiveFilters();

  const isFiltersActive = activeDrawerComponents.includes(
    DrawerComponent.FILTERS,
  );

  // Use new filter click handler if provided, otherwise fall back to legacy drawer toggle
  const handleFiltersClick = () => {
    if (onFilterClick) {
      onFilterClick();
    } else {
      toggleDrawer(DrawerComponent.FILTERS);
    }
  };

  // Determine if filters are active (new style takes precedence)
  const filtersAreActive = isNewFilterActive ?? isFiltersActive;
  const showFilterBadge = (activeFilterCount ?? 0) > 0 || hasFilters;

  // Always show filter button below desktop (including tablets)
  const shouldShowFilterButton = isBelowDesktop;

  return (
    <Box
      data-testid="sdr-toolbar"
      sx={{ width: "100%", mb: isMobile ? "4px" : "8px" }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap={isMobile ? 0.5 : 1.5}
        sx={{ width: "100%", flexWrap: "wrap" }}
      >
        <Stack
          direction="row"
          gap={isMobile ? 0.5 : 1.5}
          alignItems="center"
          sx={{ flex: 1 }}
        >
          {/* Inline Semantic Search Input */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <DatasetViewerSearch
              searchString={searchText}
              onSearchChange={onSearchChange || (() => {})}
              placeholder="Search dataset content..."
            />
          </Box>
          {shouldShowFilterButton &&
            (isMobile ? (
              <Tooltip title="Filters">
                <IconButton
                  data-testid="sdr-btn-filters"
                  data-attr="datasets:btn:toggle-filters"
                  color={
                    filtersAreActive || showFilterBadge ? "primary" : "default"
                  }
                  size="small"
                  onClick={handleFiltersClick}
                  sx={{ minWidth: 44, minHeight: 44 }}
                >
                  <Badge
                    badgeContent={activeFilterCount}
                    color="primary"
                    invisible={!activeFilterCount || activeFilterCount === 0}
                  >
                    <FilterAltOutlined />
                  </Badge>
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                data-testid="sdr-btn-filters"
                data-attr="datasets:btn:toggle-filters"
                startIcon={
                  <Badge
                    badgeContent={activeFilterCount}
                    color="primary"
                    invisible={!activeFilterCount || activeFilterCount === 0}
                    sx={{ "& .MuiBadge-badge": { right: -3, top: -3 } }}
                  >
                    <FilterAltOutlined />
                  </Badge>
                }
                color={
                  filtersAreActive || showFilterBadge ? "primary" : "inherit"
                }
                variant="text"
                onClick={handleFiltersClick}
              >
                Filters
              </Button>
            ))}
          <SearchIndexInfo datasetId={dataset.id} />
        </Stack>
        <DatasetHeaderActions
          dataset={dataset}
          overrideDatasetId={overrideDatasetId}
          hideElements={hideElements}
        />
      </Stack>
    </Box>
  );
};

export default DatasetToolBar;
